/**
 * Image helpers — downgrade large remote image URLs to responsive sizes
 * without modifying the source JSON content. Produces srcSet strings
 * suitable for <img srcSet sizes>.
 *
 * Used to fix PageSpeed "image-delivery" findings: TMDB posters/backdrops
 * and picsum.photos demo images are otherwise served at 1280–1920 px even
 * on phones.
 */

type TmdbPosterSize = "w92" | "w154" | "w185" | "w342" | "w500" | "w780" | "original";
type TmdbBackdropSize = "w300" | "w780" | "w1280" | "original";

/** Rewrite the size segment of a TMDB t/p URL. Returns input unchanged for non-TMDB. */
export function tmdbResize(url: string, size: TmdbPosterSize | TmdbBackdropSize): string {
  if (!url || !url.includes("image.tmdb.org/t/p/")) return url;
  return url.replace(/image\.tmdb\.org\/t\/p\/(w\d+|original)\//, `image.tmdb.org/t/p/${size}/`);
}

/** Rewrite picsum.photos URL to a specific width/height. */
export function picsumResize(url: string, w: number, h: number): string {
  if (!url || !url.includes("picsum.photos")) return url;
  return url.replace(/picsum\.photos\/(?:seed\/[^/]+\/)?\d+\/\d+/, (m) => {
    if (m.includes("/seed/")) {
      const seed = m.split("/seed/")[1].split("/")[0];
      return `picsum.photos/seed/${seed}/${w}/${h}`;
    }
    // fastly.picsum.photos/id/123/1600/900
    const parts = m.split("/");
    return parts.slice(0, parts.length - 2).join("/") + `/${w}/${h}`;
  });
}

/** Rewrite placehold.co URL to a specific size. */
export function placeholdResize(url: string, w: number, h: number): string {
  if (!url || !url.includes("placehold.co")) return url;
  return url.replace(/placehold\.co\/\d+x\d+/, `placehold.co/${w}x${h}`);
}

/** Build a TMDB poster srcSet "url w185, url w342, url w500". */
export function tmdbPosterSrcSet(url: string): string | undefined {
  if (!url || !url.includes("image.tmdb.org/t/p/")) return undefined;
  return [
    `${tmdbResize(url, "w185")} 185w`,
    `${tmdbResize(url, "w342")} 342w`,
    `${tmdbResize(url, "w500")} 500w`,
  ].join(", ");
}

/** Build a TMDB backdrop srcSet including a w500 step so mobile LCP fetches ~50KB instead of 200KB. */
export function tmdbBackdropSrcSet(url: string): string | undefined {
  if (!url || !url.includes("image.tmdb.org/t/p/")) return undefined;
  return [
    `${tmdbResize(url, "w300")} 300w`,
    `${tmdbResize(url, "w500")} 500w`,
    `${tmdbResize(url, "w780")} 780w`,
    `${tmdbResize(url, "w1280")} 1280w`,
  ].join(", ");
}

/** Generic small-image rewrite: downgrades any known CDN to a target width. */
export function smallImage(url: string, w: number, h: number): string {
  if (!url) return url;
  if (url.includes("image.tmdb.org/t/p/")) {
    // pick the closest TMDB size for posters: 185/342/500/780; backdrops 300/780/1280
    if (w <= 200) return tmdbResize(url, "w185");
    if (w <= 350) return tmdbResize(url, "w342");
    if (w <= 600) return tmdbResize(url, "w500");
    return tmdbResize(url, "w780");
  }
  if (url.includes("picsum.photos")) return picsumResize(url, w, h);
  if (url.includes("placehold.co")) return placeholdResize(url, w, h);
  // For any other remote image (GitHub raw, arbitrary CDN), route through
  // the free images.weserv.nl proxy which resizes + re-encodes to webp on
  // the fly. This is critical for large images hosted on GitHub raw which
  // otherwise ship at their full 1–2 MB size to mobile devices.
  return weservProxy(url, w);
}

/**
 * images.weserv.nl proxy — resizes any public HTTPS image to a target
 * width and re-encodes it to WebP with quality 78. Free, cached at the
 * edge, and safe for public assets. Skips URLs that are already proxied
 * or that live on origins the proxy cannot reach.
 */
export function weservProxy(url: string, width: number): string {
  if (!url) return url;
  if (url.startsWith("data:") || url.startsWith("blob:")) return url;
  if (url.includes("images.weserv.nl") || url.includes("wsrv.nl")) return url;
  // Strip protocol — weserv expects a bare host+path in the `url` param.
  const stripped = url.replace(/^https?:\/\//, "");
  const w = Math.max(64, Math.min(1600, Math.round(width)));
  return `https://images.weserv.nl/?url=${encodeURIComponent(stripped)}&w=${w}&output=webp&q=78`;
}

/**
 * Build a responsive srcSet for a generic remote image using the weserv
 * proxy. Emits three widths so browsers can pick an appropriately-sized
 * asset for the device pixel ratio.
 */
export function weservSrcSet(url: string, widths: number[] = [200, 400, 800]): string | undefined {
  if (!url) return undefined;
  if (url.startsWith("data:") || url.startsWith("blob:")) return undefined;
  return widths.map((w) => `${weservProxy(url, w)} ${w}w`).join(", ");
}

/** True when we can safely rewrite the URL through the weserv proxy. */
export function isExternalRemoteImage(url: string): boolean {
  if (!url) return false;
  if (url.startsWith("data:") || url.startsWith("blob:")) return false;
  if (url.startsWith("/")) return false;
  if (url.includes("image.tmdb.org")) return false;
  if (url.includes("picsum.photos")) return false;
  if (url.includes("placehold.co")) return false;
  return /^https?:\/\//.test(url);
}
